// SLA Health panel.
//
// Per-tab SLA state badge. State carries text + icon + color (never
// color-only) for accessibility.

import { AlertTriangle, CheckCircle2, CircleStop, Lock, XOctagon } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import type { SlaHealthState, TabSlaStatus } from "@/api/dealFlow/types";

interface Props {
  tabs: TabSlaStatus[];
}

const STATE_META: Record<
  SlaHealthState,
  { label: string; tone: string; icon: typeof CheckCircle2 }
> = {
  OnTrack: { label: "On track", tone: "text-status-success", icon: CheckCircle2 },
  AtRisk: { label: "At risk", tone: "text-status-warning", icon: AlertTriangle },
  Breached: { label: "Breached", tone: "text-status-error", icon: XOctagon },
  // A lane the deal hasn't entered yet has no running SLA timer — render it
  // as "Not started" (no green dot, no "On track" copy) rather than implying
  // the work is underway.
  Locked: { label: "Not started", tone: "text-gray-mid", icon: Lock },
  // The deal is Closed, Declined or Withdrawn: the clock has stopped.
  Stopped: { label: "Stopped", tone: "text-gray-mid", icon: CircleStop },
};

export function SlaHealthPanel({ tabs }: Props) {
  return (
    <Card className="p-space-4" aria-labelledby="sla-health-heading">
      <h3
        id="sla-health-heading"
        className="font-display text-h4 text-onyx uppercase tracking-wider mb-space-3"
      >
        SLA Health
      </h3>
      <ul className="flex flex-col gap-space-2">
        {tabs.map((t) => {
          const meta = STATE_META[t.state];
          const Icon = meta.icon;
          const dayText =
            t.state === "Locked" || t.state === "Stopped"
              ? "—"
              : t.currentDay !== null && t.targetDays !== null
                ? `Day ${t.currentDay} of ${t.targetDays}`
                : t.targetDays !== null
                  ? `Target ${t.targetDays} BD`
                  : "—";

          return (
            <li
              key={t.tabCode}
              className={`grid grid-cols-12 items-center gap-space-3 py-space-1 ${
                t.state === "Breached" ? "border-l-4 border-status-error pl-space-2" : ""
              }`}
            >
              <span className="col-span-5 text-body text-onyx font-semibold">
                {t.tabDisplayName}
                {t.targetDays !== null && (
                  <span className="text-small text-gray-mid font-normal ml-space-2">
                    (target {t.targetDays} BD)
                  </span>
                )}
              </span>
              <span
                className={`col-span-4 inline-flex items-center gap-space-1 ${meta.tone}`}
                aria-label={`SLA state: ${meta.label}`}
              >
                <Icon size={16} aria-hidden />
                <span className="font-semibold">{meta.label}</span>
              </span>
              <span className="col-span-3 text-small text-gray-mid text-right">
                {dayText}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
